import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const KEY = 'fabricFavorites';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch {
    return [];
  }
};

// Favorites live in the browser; when signed in they are also mirrored to the account favorites API.
export const useFabricFavorites = () => {
  const { token, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState(read);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    axios.get(`${API_URL}/user/favorite-colors`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const remote = data?.colors || [];
        setFavorites((local) => {
          const merged = [...local];
          remote.forEach((r) => {
            if (!merged.some((f) => f.id === r.color_id)) {
              merged.push({ id: r.color_id, name: r.color_name, code: r.color_code, hex_color: r.hex_color, image_url: r.image_url, brand_name: r.brand_name, collection_name: r.collection_name, material_name: r.material_name });
            }
          });
          return merged;
        });
      })
      .catch(() => {});
  }, [isAuthenticated, token]);

  const isFavorite = useCallback((id) => favorites.some((f) => f.id === id), [favorites]);

  const toggleFavorite = useCallback((entry) => {
    const exists = favorites.some((f) => f.id === entry.id);
    setFavorites((prev) => (exists ? prev.filter((f) => f.id !== entry.id) : [...prev, entry]));
    if (isAuthenticated && token) {
      const headers = { Authorization: `Bearer ${token}` };
      const req = exists
        ? axios.delete(`${API_URL}/user/favorite-colors/${entry.id}`, { headers })
        : axios.post(`${API_URL}/user/favorite-colors`, {
            color_id: entry.id, color_name: entry.name, color_code: entry.code, hex_color: entry.hex_color,
            image_url: entry.image_url, material_name: entry.material_name, collection_name: entry.collection_name, brand_name: entry.brand_name,
          }, { headers });
      req.catch(() => {});
    }
    return !exists;
  }, [favorites, isAuthenticated, token]);

  return { favorites, isFavorite, toggleFavorite };
};
